import { gql, useLazyQuery } from "@apollo/client";
import type { Product, ProductEdge, ProductVariantConnection } from "@shopify/hydrogen-react/storefront-api-types";
import qs from "query-string";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import type { ICollectionHandleQuery } from "@repo/shopify/queries/collection-gql";
import { collectionHandleQuery } from "@repo/shopify/queries/collection-gql";
import type { IProductMetafields } from "@repo/shopify/queries/product-gql";

import { COLLECTION_SORT } from "@const/constant";
import { DEFAULT_TITLE } from "@const/meta";
import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useLanguage from "@hooks/useLanguage";

import { useLayoutStore } from "@store/layout";

const useCollections = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const [searchParams] = useSearchParams();

  const { lang } = useLanguage();

  const { openToast, setMeta } = useLayoutStore();

  const [collectionData, setCollectionData] = useState<ICollectionHandleQuery>();
  const [products, setProducts] = useState<ProductEdge[]>([]);
  const [expandProducts, setExpandProducts] = useState<ProductEdge[]>([]);

  const handle = pathname.includes("collections") && pathname.split("/").pop();

  const handleRef = useRef<string>("");
  const appliedData = useRef<ICollectionHandleQuery | null>(null);

  const hasNextPage = useMemo(
    () => collectionData?.collection.products.pageInfo.hasNextPage || false,
    [collectionData?.collection.products.pageInfo],
  );

  const endCursor = useMemo(() => collectionData?.collection.products.pageInfo.endCursor, [collectionData?.collection.products.pageInfo]);

  const variables = useMemo(() => {
    const queryString = qs.parse(search);

    // category / color / size
    const sortQueries = COLLECTION_SORT.find((item) => item.name === queryString.sort);
    const filterQueries = Object.entries(queryString)
      .filter(([key]) => key === "category" || key === "color" || key === "size" || key === "tag")
      .map(([key, value]) => {
        const parseValue = JSON.parse((value as string) || "[]") as string[];
        return parseValue.map((item) => {
          switch (key) {
            case "category":
              return { productType: item };
            case "tag":
              return { tag: item };
            default:
              return { variantOption: { name: key, value: item } };
          }
        });
      })
      .flat();

    return {
      first: queryString.view === "seperate" ? 12 : 25, // 쿼리 1회에 조회하는 product 수
      handle,
      sortKey: sortQueries?.value ?? "COLLECTION_DEFAULT",
      reverse: sortQueries?.reverse ?? false,
      filters: filterQueries,
    };
  }, [handle, search]);

  const prevVariables = useRef(variables);

  const getExpandList = (productList: ProductEdge[]) => {
    const expandList: ProductEdge[] = [];

    productList.forEach(({ node }) => {
      const colorOptions = node.options.find((item) => item.name === "Color")?.values;

      if (colorOptions) {
        colorOptions.forEach((color) => {
          const colorList = node.variants.edges.filter(({ node: variantNode }) =>
            variantNode.selectedOptions.find((option) => option.value === color),
          );

          const newList: ProductEdge = {
            node: {
              ...node,
              options: node.options.map((option) => (option.name === "Color" ? { ...option, values: [color] } : option)),
              variants: { edges: colorList } as ProductVariantConnection,
              availableForSale: colorList.every((item) => item.node.availableForSale),
            },
          } as ProductEdge;

          expandList.push(newList);
        });
      }
    });

    return expandList;
  };

  const [fetchCollection, { data, loading, called, fetchMore, refetch }] = useLazyQuery<ICollectionHandleQuery>(
    gql(collectionHandleQuery.query),
    {
      variables,
      notifyOnNetworkStatusChange: true,
      onError: (error) => {
        openToast(NOTIFICATIONS.ERROR_SERVER);
        throw new Error(error.message);
      },
    },
  );

  useEffect(() => {
    void fetchCollection();
  }, []);

  // 필터·정렬이 바뀌면 캐시가 아니라 서버 응답을 다시 받는다.
  // 필터 count는 조합마다 값이 달라지는데 cache-first로 읽으면 이전 조합의 값이 그대로 남는다
  useEffect(() => {
    if (JSON.stringify(prevVariables.current) === JSON.stringify(variables)) return;

    prevVariables.current = variables;
    void refetch(variables);
  }, [variables]);

  useEffect(() => {
    if (handleRef.current !== "" && handle !== handleRef.current) {
      appliedData.current = null;
      setCollectionData(undefined);
      setProducts([]);
      setExpandProducts([]);
    }

    if (handle) handleRef.current = handle;
  }, [handle]);

  useEffect(() => {
    if (!data || appliedData.current === data) return;

    if (!data.collection) {
      navigate(URLS.HOME);
      return;
    }

    appliedData.current = data;

    const edges = data.collection.products.edges.filter(({ node }) => (node as Product & IProductMetafields)?.hidden?.value !== "true");

    setCollectionData(data);
    setProducts(edges);
    setExpandProducts(getExpandList(edges));
  }, [data]);

  useEffect(() => {
    setMeta({
      title: `${collectionData?.collection ? `${collectionData?.collection?.title} - ` : ""} ${DEFAULT_TITLE}`,
    });
  }, [collectionData]);

  const handleFetchMore = async () => {
    await fetchMore({
      variables: { ...variables, after: endCursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          collection: {
            ...prev.collection,
            products: {
              ...prev.collection.products,
              edges: [...(prev.collection?.products?.edges || []), ...(fetchMoreResult.collection?.products?.edges || [])],
              pageInfo: fetchMoreResult.collection.products.pageInfo,
            },
          },
        };
      },
    });
  };

  const visibleProducts = useMemo(
    () =>
      searchParams.get("view") === "combined"
        ? products
        : expandProducts.filter(({ node }) => (lang !== "jp" && pathname.includes("archive") ? node.availableForSale : true)),
    [searchParams, products, expandProducts, pathname],
  );

  return {
    collectionData,
    loading: loading || !called,
    products: visibleProducts,
    productCount: products.length,
    hasNextPage,
    handleFetchMore,
  };
};

export default useCollections;
